import React, { createContext, useContext, useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  User,
} from "firebase/auth";
import { auth, db, createNewUserData } from "../../firebase/config";
import { doc, setDoc } from "firebase/firestore";
import { logUserLogin } from "../../firebase/logsService";

// Fonction pour obtenir l'adresse IP du client
const getClientIP = async (): Promise<string> => {
  try {
    // Essayer d'obtenir l'IP publique via plusieurs services
    const services = [
      'https://api.ipify.org?format=json',
      'https://ipapi.co/json/',
      'https://api.my-ip.io/ip.json'
    ];
    
    for (const service of services) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch(service, { 
          signal: controller.signal,
          headers: {
            'Accept': 'application/json'
          }
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          
          // Extraire l'IP selon le format de réponse du service
          let ip = data.ip || data.query || data.IPv4;
          
          if (ip && ip !== '127.0.0.1' && !ip.startsWith('192.168.') && !ip.startsWith('10.')) {
            return ip;
          }
        }
      } catch (serviceError) {
        console.warn(`Service ${service} failed:`, serviceError);
        continue;
      }
    }
    
    // Si aucun service ne fonctionne, générer une IP aléatoire pour la démo
    const randomIP = `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`;
    return randomIP;
    
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'IP:', error);
    // Générer une IP aléatoire pour la démo
    const randomIP = `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`;
    return randomIP;
  }
};

interface AuthContextType {
  currentUser: User | null;
  signup: (email: string, password: string, name: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  signup: async () => {},
  login: async () => {},
  logout: async () => {},
  resetPassword: async () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const signup = async (email: string, password: string, name: string) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      const newUserData = createNewUserData({
        name: name,
        email: email
      });
      
      await setDoc(doc(db, "users", user.uid), newUserData);

      setCurrentUser(user);
    } catch (error) {
      console.error("Error during signup:", error);
      throw error;
    }
  };

  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    setCurrentUser(userCredential.user);
  };

  const logout = async () => {
    await signOut(auth);
    setCurrentUser(null);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const previousUser = currentUser;
      setCurrentUser(user);
      setLoading(false);
      
      // Si un utilisateur vient de se connecter (et ce n'est pas le chargement initial)
      if (user && !previousUser && user.email) {
        try {
          const clientIP = await getClientIP();
          await logUserLogin(user.email, clientIP);
        } catch (error) {
          console.error('Erreur lors de l\'enregistrement automatique de la connexion:', error);
        }
      }
    });

    return unsubscribe;
  }, [currentUser]);

  const value = {
    currentUser,
    signup,
    login,
    logout,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
